import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { AchPaymentMethod, Card } from 'src/app/models/payment.model';
import { TableColumn } from 'src/app/models/table-columns';
import { PaymentMethodsService } from 'src/app/services/paymentmethod.service';
import { ScrBaseComponent } from '../../../base.component';


@Component({
  selector: 'app-payment-method',
  templateUrl: './payment-method.component.html',
  styleUrls: ['./payment-method.component.scss']
})
export class PaymentMethodComponent extends ScrBaseComponent implements OnInit {

  addCardBool = false;
  updateCardBool = false;
  updateAchBool = false;
  paymentMethods: (AchPaymentMethod | Card)[] = [];
  cardToEditObj!: Card;
  achToEditObj!: AchPaymentMethod;
  expandedRowKeys: Record<string,boolean> = {};

  @ViewChild('brandProfileForm') creditCardForm?: NgForm;
  @ViewChild('achProfileForm') achForm?: NgForm;
  @ViewChild('paymentTable') paymentTable!: Table;
  tableColumn: TableColumn[] = [
    { field: 'cardType', width: '5' },
    { field: 'accountNumber', width: '5' },
    { field: 'description', width: '7' },
    { field: 'expDate', width: '4' },
    { field: 'default', width: '5' },
    { field: 'edit', width: '4' },
    { field: 'remove', width: '3' }
  ];

  constructor(
    private confirmationService: ConfirmationService,
    private paymentMethodService: PaymentMethodsService) {
    super();
  }

  get hasDefault(): boolean {
    return this.paymentMethods.some(item => !!item.default);
  }

  ngOnInit(): void {
    this.localizeTableColumns(this.tableColumn);
    this.retrievePaymentMethods();

  }

  displayAddPaymentMethod() {
    this.clearMessages();
    this.addCardBool = true;
    this.expandedRowKeys = this.paymentTable?.expandedRowKeys;
  }

  retrievePaymentMethods(successMessage?: string, successMessageCode?: string, itemToExpand?: string|number) {
    this.clearMessages();
    this.observe(this.paymentMethodService.retrievePaymentMethods(), res => {
      if (res.creditcard || res.ach) {
        if (successMessageCode) {
          this.displayLocalizedSuccess(successMessageCode);
        }
        else if (successMessage) {
          this.displaySuccess(successMessage, true);
        }
        // Reset Methods
        this.paymentMethods = [];
        res.ach?.forEach(ach => {
          ach.cardType = 'ACH';
          ach.default = ach.defaultPaymentMethod;
          ach.uniqueId = ach.achPaymentMethodId;
          this.paymentMethods.push(ach);
        });
        res.creditcard?.forEach(cc => {
          cc.uniqueId = cc.creditCardId;
          this.paymentMethods.push(cc);
        });
        // console.debug('expanded: ' + JSON.stringify(this.expandedRowKeys));
        if (this.expandedRowKeys) {
          // console.log(this.expandedRowKeys);
          this.paymentTable.expandedRowKeys = this.expandedRowKeys;
        }
        if (itemToExpand) {
          const key = `${itemToExpand}`;
          if (this.paymentTable.expandedRowKeys[key] === undefined) {
            this.paymentTable.expandedRowKeys[key] = true;
          }
        }
      }
      return false; // we will handle the messages.
    });
  }

  isDateExpired(month: number, year: number) {
    const cardDate = new Date(year, month, 0, 23, 59, 59, 999);
    const currentDate = new Date();
    if (cardDate < currentDate) {
      return 'redFont';
    }
    return '';
  }

  protected override hideChildren(): void {
    this.clearMessages();
    this.addCardBool = false;
    this.updateCardBool = false;
    this.updateAchBool = false;
  }

  protected override reloadList(message?: string, messageCode?: string, value?: number|string): void {
    this.retrievePaymentMethods(message, messageCode, value);
  }

  cardToEdit(card: Card) {
    this.clearMessages();
    this.cardToEditObj = card;
    if (this.cardToEditObj.uniqueId) {
      this.expandedRowKeys[this.cardToEditObj.uniqueId] = true;
    }
    this.updateCardBool = true;
    this.updateAchBool = false;
  }

  achToEdit(ach: AchPaymentMethod) {
    this.clearMessages();
    this.achToEditObj = ach;
    if (this.achToEditObj.uniqueId) {
      this.expandedRowKeys[this.achToEditObj.uniqueId] = true;
    }
    this.updateCardBool = false;
    this.updateAchBool = true;
  }

  removeCreditCard(card: Card) {
    let message = '';
    if (this.paymentMethods.length === 1) {
      message = 'You will no longer have a payment method on record. If you wish to have auto-pay or auto-renew, consider adding a new payment method to your account and setting it as the default.';
    } else {
      if (card.default) {
        message = 'Consider selecting another payment method as the default if you want to have auto-pay or auto-renew.';
      } else {
        message = 'Select OK to remove the payment method or click cancel.';
      }
    }
    this.clearMessages();
    this.confirmationService.confirm({
      message,
      header: 'Confirm Removal of Payment Method',
      key: 'confirmDelete',
      accept: () => {
        this.observe(this.paymentMethodService.removeCreditCard(card.creditCardId), res => {
          if (res.status === 'success') {
            let successMessage: string | undefined = undefined;
            if (res.messages && res.messages.length > 0) {
              successMessage = res.messages[0].message;
            }
            this.retrievePaymentMethods(successMessage);
          }
          else if (res.messages) {
            this.processScrErrorResponse(res, true);
          }
          return false;
        });
      }
    });
  }

  setAsDefaultCard(card: Card) {
    this.clearMessages();
    this.confirmationService.confirm({
      message: 'This payment method will become the default payment method. This means that the existing default payment method, if one exists, will no longer be the default. <br /> Do you want to proceed to designate this payment method as the default?',
      header: 'Set Default Payment Method',
      key: 'confirmDefault',
      accept: () => {
        this.observe(this.paymentMethodService.setAsDefaultCard(card.creditCardId), res => {
          if (res.status === 'success') {
            let successMessage: string | undefined = undefined;
            if (res.messages && res.messages.length > 0) {
              successMessage = res.messages[0].message;
            }
            this.retrievePaymentMethods(successMessage);
          }
          else if (res.messages) {
            this.processScrErrorResponse(res, true);
          }
          return false;
        });
      }
    });
  }

  setAsDefaultAch(card: AchPaymentMethod) {
    this.clearMessages();
    this.confirmationService.confirm({
      message: 'This payment method will become the default payment method. This means that the existing default payment method, if one exists, will no longer be the default. <br /> Do you want to proceed to designate this payment method as the default?',
      header: 'Set Default Payment Method',
      key: 'confirmDefault',
      accept: () => {
        this.observe(this.paymentMethodService.setAsDefaultPaymentMethod(card.achPaymentMethodId), res => {
          if (res.status === 'success') {
            let successMessage: string | undefined = undefined;
            if (res.messages && res.messages.length > 0) {
              successMessage = res.messages[0].message;
            }
            this.retrievePaymentMethods(successMessage);
          }
          else if (res.messages) {
            this.processScrErrorResponse(res, true);
          }
          return false;
        });
      }
    });
  }


  removeAch(ach: AchPaymentMethod) {
    let message = '';
    if (this.paymentMethods.length === 1) {
      message = 'You will no longer have a payment method on record. If you wish to have auto-pay or auto-renew, consider adding a new payment method to your account and setting it as the default.';
    } else {
      if (ach.default) {
        message = 'Consider selecting another payment method as the default if you want to have auto-pay or auto-renew.';
      } else {
        message = 'Select OK to remove the payment method or click cancel.';
      }
    }
    this.clearMessages();
    this.confirmationService.confirm({
      message,
      header: 'Confirm Removal of Payment Method',
      key: 'confirmDelete',
      accept: () => {
        this.observe(this.paymentMethodService.removeAchPaymentMethod(ach.achPaymentMethodId), res => {
          if (res.status === 'success') {
            let successMessage: string | undefined = undefined;
            if (res.messages && res.messages.length > 0) {
              successMessage = res.messages[0].message;
            }
            this.retrievePaymentMethods(successMessage);
          }
          else if (res.messages) {
            this.processScrErrorResponse(res, true);
          }
          return false;
        });
      }
    });
  }

}
